import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { useSystem, SYSTEM_META, SERVICE_META } from "@/lib/system";
import { Badge, StatusDot } from "@/components/Bits";
import { fmtCompact, fmtMoney, fmtDateTime, STATUS_META, FINAL_STATUS_META } from "@/lib/format";
import { Search, Plus, Filter, ArrowUpRight, Download, Shuffle, RefreshCw } from "lucide-react";

const STATUSES = ["NEW", "IN_PROGRESS", "ESCALATED_TO_BANK", "AWAITING_FINAL_STATUS", "RESOLVED", "CLOSED"];

function SlaPill({ ticket }) {
  const { sla } = ticket;
  // priority: bank breach > response breach > warnings
  if (sla.bank_level === "breach") return <Badge color="error">BANK · {fmtCompact(sla.time_since_escalated_sec)}</Badge>;
  if (sla.bank_level === "warning") return <Badge color="warning">BANK · {fmtCompact(sla.time_since_escalated_sec)}</Badge>;
  if (sla.bank_level === "ok" && sla.time_since_escalated_sec != null) return <Badge color="awaiting">BANK · {fmtCompact(sla.time_since_escalated_sec)}</Badge>;
  if (sla.response_level === "breach") return <Badge color="error">REPLY · {fmtCompact(sla.time_to_first_response_sec)}</Badge>;
  if (sla.response_level === "warning") return <Badge color="warning">REPLY · {fmtCompact(sla.time_to_first_response_sec)}</Badge>;
  return <Badge color="success">OK</Badge>;
}

export default function Tickets() {
  const { system } = useSystem();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [txnType, setTxnType] = useState("");
  const [merchant, setMerchant] = useState(params.get("merchant") || "");
  const [serviceType, setServiceType] = useState("");
  const [paymentChannel, setPaymentChannel] = useState("");
  const [merchants, setMerchants] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [systemMismatch, setSystemMismatch] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/merchants", { params: system ? { system } : {} }).then((r) => setMerchants(r.data));
  }, [system]);

  const load = async () => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (status) params.status = status;
    if (customerType) params.customer_type = customerType;
    if (txnType) params.transaction_type = txnType;
    if (serviceType) params.service_type = serviceType;
    if (paymentChannel) params.payment_channel = paymentChannel;
    if (merchant) params.merchant = merchant;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (system) params.system = system;
    if (systemMismatch) params.system_mismatch = true;
    const { data } = await api.get("/tickets", { params });
    // Paginated response
    setTickets(data.items || []);
    setTotal(data.total || 0);
    setPages(data.pages || 1);
    setLoading(false);
  };

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [status, customerType, txnType, serviceType, paymentChannel, merchant, dateFrom, dateTo, system, systemMismatch, pageSize, q]);

  useEffect(() => {
    load();
    // Auto-refresh removed per user request — use manual Refresh button.
    // eslint-disable-next-line
  }, [status, customerType, txnType, serviceType, paymentChannel, merchant, dateFrom, dateTo, system, systemMismatch, page, pageSize]);

  const counts = useMemo(() => {
    const c = { ALL: total };
    STATUSES.forEach((s) => (c[s] = 0));
    tickets.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tickets, total]);

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="tickets-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Tickets</div>
          <h1 className="text-2xl font-bold tracking-tight">All Tickets</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="refresh-tickets-btn"
            onClick={load}
            disabled={loading}
            title="Refresh (filter dipertahankan)"
            className="border border-line hover:bg-surface2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-cyan" : ""}`} /> Refresh
          </button>
          <button
            data-testid="new-ticket-btn"
            onClick={() => nav("/tickets/new")}
            className="bg-cyan hover:bg-cyan-dark text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        </div>
      </div>

      {/* Export bar */}
      <div className="flex items-center justify-between gap-2 bg-surface border border-line rounded-sm px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400" data-testid="ticket-count-wrap">
          <span data-testid="ticket-count">{tickets.length}</span> of <span data-testid="ticket-total">{total}</span> tickets · page <span data-testid="ticket-page">{page}</span>/<span data-testid="ticket-pages">{pages}</span>
        </div>
        <div className="flex items-center gap-2">
          <BulkReassignButton
            filter={{ system, merchant_name: merchant, status, service_type: serviceType }}
            onDone={load}
            count={total}
          />
          <ExportButton
            params={{ status, customer_type: customerType, transaction_type: txnType, service_type: serviceType, payment_channel: paymentChannel, merchant, date_from: dateFrom, date_to: dateTo, system }}
          />
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="status-tabs">
        <button
          data-testid="tab-status-ALL"
          onClick={() => setStatus("")}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border whitespace-nowrap transition-colors ${
            status === "" ? "bg-cyan/10 border-cyan/40 text-cyan" : "border-line text-slate-400 hover:bg-surface"
          }`}
        >
          All <span className="ml-1 font-mono">{counts.ALL}</span>
        </button>
        {STATUSES.map((sk) => (
          <button
            key={sk}
            data-testid={`tab-status-${sk}`}
            onClick={() => setStatus(sk)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border whitespace-nowrap transition-colors ${
              status === sk ? "bg-cyan/10 border-cyan/40 text-cyan" : "border-line text-slate-400 hover:bg-surface"
            }`}
          >
            {STATUS_META[sk].label} <span className="ml-1 font-mono">{counts[sk] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-surface border border-line rounded-sm p-3 flex flex-wrap gap-2">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex-1 min-w-[240px] relative"
        >
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            data-testid="ticket-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari merchant order, platform order, RRN, VA, merchant, customer..."
            className="w-full bg-bg border border-line rounded-sm pl-9 pr-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40"
          />
        </form>
        <select
          data-testid="filter-customer-type"
          value={customerType}
          onChange={(e) => setCustomerType(e.target.value)}
          className="bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none"
        >
          <option value="">All Customer</option>
          <option value="FOREX">Forex</option>
          <option value="GAMING">Gaming</option>
        </select>
        <select
          data-testid="filter-txn-type"
          value={txnType}
          onChange={(e) => setTxnType(e.target.value)}
          className="bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none"
        >
          <option value="">All Type</option>
          <option value="QRIS">QRIS</option>
          <option value="VA">VA</option>
        </select>
        <select
          data-testid="filter-merchant"
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          className="bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none min-w-[180px]"
        >
          <option value="">All Merchants</option>
          {merchants.map((m) => <option key={m.id} value={m.name}>{m.name} ({m.ticket_count})</option>)}
        </select>
        <select
          data-testid="filter-service-type"
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value)}
          className="bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none"
        >
          <option value="">All Services</option>
          <option value="PAYIN">Pay-In</option>
          <option value="PAYOUT">Payout</option>
          <option value="WITHDRAWAL">Withdrawal</option>
          <option value="TOPUP">Top-Up</option>
        </select>
        <select
          data-testid="filter-channel"
          value={paymentChannel}
          onChange={(e) => setPaymentChannel(e.target.value)}
          className="bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none"
        >
          <option value="">All Channels</option>
          <option value="Ayolinx">Ayolinx</option>
          <option value="Pakailink">Pakailink</option>
          <option value="Pearpaygame">Pearpaygame</option>
          <option value="Easylink">Easylink</option>
        </select>
        <input
          data-testid="filter-date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-2 text-xs focus:border-cyan focus:outline-none"
          title="Date from"
        />
        <input
          data-testid="filter-date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-2 text-xs focus:border-cyan focus:outline-none"
          title="Date to"
        />
        <button
          data-testid="filter-system-mismatch"
          type="button"
          onClick={() => setSystemMismatch((v) => !v)}
          title="Tampilkan tiket dengan system yang tidak sesuai master Excel"
          className={`px-3 py-2 text-[11px] uppercase tracking-wider rounded-sm border whitespace-nowrap flex items-center gap-1.5 ${
            systemMismatch ? "bg-amber-500/20 border-amber-500/50 text-amber-300" : "bg-bg border-line text-slate-400 hover:bg-surface2"
          }`}
        >
          ⚠️ System Mismatch
        </button>
      </div>

      {/* Table */}
      <div className="bg-surface border border-line rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tickets-table">
            <thead className="bg-surface2 border-b border-line">
              <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <th className="text-left px-3 py-2.5 font-medium">Ticket</th>
                <th className="text-left px-3 py-2.5 font-medium">System</th>
                <th className="text-left px-3 py-2.5 font-medium">Service</th>
                <th className="text-left px-3 py-2.5 font-medium">Merchant Order</th>
                <th className="text-left px-3 py-2.5 font-medium">Platform Order</th>
                <th className="text-left px-3 py-2.5 font-medium">Type</th>
                <th className="text-left px-3 py-2.5 font-medium">Merchant</th>
                <th className="text-right px-3 py-2.5 font-medium">Amount</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium">SLA</th>
                <th className="text-left px-3 py-2.5 font-medium">Assignee</th>
                <th className="text-left px-3 py-2.5 font-medium">Created</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="text-center py-10 text-slate-500">Loading...</td></tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={12} className="text-center py-12 text-slate-500" data-testid="empty-tickets">
                  Tidak ada tiket. Klik &quot;New Ticket&quot; untuk membuat.
                </td></tr>
              )}
              {tickets.map((t) => (
                <tr
                  key={t.id}
                  data-testid={`ticket-row-${t.ticket_number}`}
                  onClick={() => nav(`/tickets/${t.id}`)}
                  className="border-b border-line/40 hover:bg-surface2 cursor-pointer"
                >
                  <td className="px-3 py-2.5 font-mono text-xs text-cyan">{t.ticket_number}</td>
                  <td className="px-3 py-2.5">
                    <Badge color={SYSTEM_META[t.system]?.color || "muted"}>{SYSTEM_META[t.system]?.short || "—"}</Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge color={SERVICE_META[t.service_type]?.color || "muted"}>{SERVICE_META[t.service_type]?.label || t.service_type || "—"}</Badge>
                    {t.merchant_group && (
                      <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{t.merchant_group}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs" data-testid="td-merchant-order">
                    <div className="text-emerald-300">{t.merchant_order_number || <span className="text-slate-600">—</span>}</div>
                    <div className="text-slate-500 text-[10px]">RRN: {t.rrn_number || t.va_number || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs" data-testid="td-platform-order">
                    <div className="text-cyan break-all">{t.platform_order_number || <span className="text-slate-600">—</span>}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge color={t.transaction_type === "QRIS" ? "info" : "awaiting"}>{t.transaction_type}</Badge>
                    <div className="text-[10px] text-slate-500 mt-0.5 uppercase">{t.customer_type}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <div className="text-white">{t.merchant_name || "—"}</div>
                    <div className="text-slate-500 text-[10px]">{t.payment_channel}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-right text-xs">{fmtMoney(t.amount)}</td>
                  <td className="px-3 py-2.5">
                    <Badge color={STATUS_META[t.status]?.color}>{STATUS_META[t.status]?.label}</Badge>
                    {t.final_status && (
                      <div className="mt-1">
                        <Badge color={FINAL_STATUS_META[t.final_status]?.color}>{FINAL_STATUS_META[t.final_status]?.label}</Badge>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5"><SlaPill ticket={t} /></td>
                  <td className="px-3 py-2.5 text-xs">{t.assignee?.name || <span className="text-slate-600">Unassigned</span>}</td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-400">{fmtDateTime(t.created_at)}</td>
                  <td className="px-3 py-2.5"><ArrowUpRight className="w-3.5 h-3.5 text-slate-600" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between gap-2 bg-surface border border-line rounded-sm px-3 py-2" data-testid="pagination-controls">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400">
          <span>Rows</span>
          <select
            data-testid="page-size-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-surface2 border border-line rounded-sm px-2 py-1 text-xs text-white"
          >
            {[25, 50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="ml-3">
            {total === 0 ? 0 : (page - 1) * pageSize + 1}
            {" – "}
            {Math.min(total, page * pageSize)}
            {" of "}
            {total}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="page-first-btn"
            onClick={() => setPage(1)}
            disabled={page <= 1 || loading}
            className="border border-line hover:bg-surface2 px-2 py-1 rounded-sm text-xs disabled:opacity-40"
          >« First</button>
          <button
            data-testid="page-prev-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="border border-line hover:bg-surface2 px-2 py-1 rounded-sm text-xs disabled:opacity-40"
          >‹ Prev</button>
          <span className="text-[11px] text-slate-300 px-2 font-mono" data-testid="page-indicator">
            {page} / {pages}
          </span>
          <button
            data-testid="page-next-btn"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages || loading}
            className="border border-line hover:bg-surface2 px-2 py-1 rounded-sm text-xs disabled:opacity-40"
          >Next ›</button>
          <button
            data-testid="page-last-btn"
            onClick={() => setPage(pages)}
            disabled={page >= pages || loading}
            className="border border-line hover:bg-surface2 px-2 py-1 rounded-sm text-xs disabled:opacity-40"
          >Last »</button>
        </div>
      </div>
    </div>
  );
}

function ExportButton({ params }) {
  const onClick = async () => {
    try {
      const search = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => { if (v) search.append(k, v); });
      const url = `${API_BASE}/export/tickets.csv?${search.toString()}`;
      const tok = localStorage.getItem("tara_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      const fname = resp.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "tickets.csv";
      a.download = fname;
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      alert("Gagal export: " + e.message);
    }
  };
  return (
    <button
      data-testid="export-csv-btn"
      onClick={onClick}
      className="border border-cyan/40 text-cyan hover:bg-cyan/10 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1.5 transition-colors"
    >
      <Download className="w-3 h-3" />
      Export CSV
    </button>
  );
}

function BulkReassignButton({ filter, count, onDone }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("TARAPAY");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const hasFilter = !!(filter.system || filter.merchant_name || filter.status || filter.service_type);

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const body = {};
      if (filter.system) body.system = filter.system;
      if (filter.merchant_name) body.merchant_name = filter.merchant_name;
      if (filter.status) body.status = filter.status;
      if (filter.service_type) body.service_type = filter.service_type;
      body.set_system = target;
      const { data } = await api.post("/tickets/bulk-update", body);
      setResult(data);
      if (onDone) onDone();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="bulk-reassign-btn"
        onClick={() => { setOpen(true); setResult(null); }}
        disabled={!hasFilter}
        title={hasFilter ? "Move filtered tickets to another system" : "Pilih minimal 1 filter (system/merchant/status/service)"}
        className="border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Shuffle className="w-3 h-3" />
        Bulk Reassign
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid="bulk-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-md space-y-3">
            <h3 className="text-lg font-bold">Bulk Reassign System</h3>
            <p className="text-xs text-slate-400">Pindahkan <span className="font-mono text-cyan">{count}</span> tiket (sesuai filter aktif) ke system lain. Aksi ini tidak dapat di-undo otomatis.</p>
            <div className="border border-line rounded-sm p-3 bg-bg space-y-1 text-[11px] font-mono">
              <div className="text-slate-500 text-[10px] uppercase tracking-wider">Active filter</div>
              {filter.system && <div>system = <span className="text-cyan">{filter.system}</span></div>}
              {filter.merchant_name && <div>merchant = <span className="text-cyan">{filter.merchant_name}</span></div>}
              {filter.status && <div>status = <span className="text-cyan">{filter.status}</span></div>}
              {filter.service_type && <div>service = <span className="text-cyan">{filter.service_type}</span></div>}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Target System</label>
              <select data-testid="bulk-target-system" value={target} onChange={(e) => setTarget(e.target.value)} className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none mt-1">
                <option value="TARAPAY">Tarapay</option>
                <option value="SATURNUS">Saturnuspay</option>
              </select>
            </div>
            {result && (
              <div className={`border rounded-sm p-2 text-xs ${result.error ? "border-rose-500/30 bg-rose-500/10 text-rose-400" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"}`} data-testid="bulk-result">
                {result.error ? `Error: ${result.error}` : `✓ Modified ${result.modified} of ${result.matched} tickets.`}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Close</button>
              {!result && (
                <button data-testid="bulk-confirm-btn" onClick={submit} disabled={busy} className="bg-amber-500 text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-amber-400 disabled:opacity-60">
                  {busy ? "Moving..." : `Move to ${target}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
